import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle, Clock, MapPin, Star, Users, Shield } from 'lucide-react';
import { hero, about, services, cta, posts, testimonials, siteInfo } from '../data/mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section Moderno */}
      <section
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(26, 188, 156, 0.95), rgba(20, 160, 133, 0.9)), url('${hero.backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Animated background particles */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-lime-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute top-40 right-20 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '700ms'}}></div>
          <div className="absolute bottom-20 left-40 w-72 h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '1000ms'}}></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl">
          {/* Badge de confiança */}
          <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6 animate-bounce border border-white/30">
            <CheckCircle className="w-5 h-5 text-lime-300 mr-2" />
            <span className="text-sm font-semibold">✨ 18.000+ Famílias Atendidas com Excelência</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight drop-shadow-2xl">
            {hero.title}
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-teal-50 font-light drop-shadow-lg">
            {hero.subtitle}
          </p>

          {/* CTAs modernos */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="group relative inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-lime-400 via-lime-500 to-lime-600 hover:from-lime-500 hover:via-lime-600 hover:to-lime-700 text-gray-900 px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl text-sm sm:text-base font-bold transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-lime-500/60"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Ligar Agora</span>
            </a>
            <a
              href="https://wa.me/552141064022?text=Ol%C3%A1!%20Vim%20pelo%20site,%20gostaria%20de%20informa%C3%A7%C3%B5es"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl text-sm sm:text-base font-bold transition-all duration-300 hover:scale-105 border-2 border-white/30 shadow-xl"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mt-8">
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-lime-300" />
              <span className="text-xs sm:text-sm font-semibold">24h Disponível</span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-lime-300" />
              <span className="text-xs sm:text-sm font-semibold">Meier - Zona Norte</span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-lime-300" />
              <span className="text-xs sm:text-sm font-semibold">Atendimento Imediato</span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 sm:px-5 sm:py-3 rounded-xl border border-white/20">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
              <span className="text-xs sm:text-sm font-semibold">4.8/5 - 150 Avaliações</span>
            </div>
          </div>

          {/* Urgência */}
          <div className="mt-8 inline-block bg-red-500/90 backdrop-blur-sm px-6 py-3 rounded-full animate-pulse shadow-xl">
            <p className="text-sm font-bold">⚡ Atendimento de Emergência Disponível AGORA</p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Stats Section - Nova */}
      <section className="py-12 bg-gradient-to-r from-teal-600 to-lime-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div className="transform hover:scale-110 transition-transform duration-300">
              <div className="text-5xl font-bold mb-2">18k+</div>
              <div className="text-sm opacity-90">Famílias Atendidas</div>
            </div>
            <div className="transform hover:scale-110 transition-transform duration-300">
              <div className="text-5xl font-bold mb-2">24/7</div>
              <div className="text-sm opacity-90">Atendimento</div>
            </div>
            <div className="transform hover:scale-110 transition-transform duration-300">
              <div className="text-5xl font-bold mb-2">4.8★</div>
              <div className="text-sm opacity-90">Avaliação Média</div>
            </div>
            <div className="transform hover:scale-110 transition-transform duration-300">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-sm opacity-90">Suporte Completo</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quem Somos - Modernizado */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-teal-100 text-teal-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                Sobre Nós
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{about.title}</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {about.description}
              </p>
              <Link
                to="/quem-somos"
                className="inline-flex items-center bg-gradient-to-r from-teal-500 to-lime-500 hover:from-teal-600 hover:to-lime-600 text-white px-8 py-4 rounded-xl text-sm font-bold transition-all duration-300 hover:shadow-xl hover:scale-105 group"
              >
                {about.ctaText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src={about.image}
                  alt="Quem Somos"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-teal-500 to-lime-500 text-white p-6 rounded-2xl shadow-2xl">
                  <div className="text-3xl font-bold">18.000+</div>
                  <div className="text-sm">Famílias Atendidas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Atuação - Serviços Modernizados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-teal-100 text-teal-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              Nossos Serviços
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{services.title}</h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              {services.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.items.map((service) => (
              <div
                key={service.id}
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link
                    to={service.link}
                    className="inline-flex items-center text-teal-600 hover:text-lime-600 font-semibold transition-colors group"
                  >
                    Saiba mais 
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section Modernizada */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-teal-500 to-lime-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-lime-200 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{cta.title}</h2>
          <p className="text-xl mb-8 text-teal-50">{cta.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contato"
              className="inline-flex items-center justify-center bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-bold transition-all duration-300 hover:shadow-2xl hover:scale-105 group"
            >
              {cta.ctaText}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="inline-flex items-center justify-center bg-lime-500 hover:bg-lime-600 text-white px-8 py-4 rounded-xl text-lg font-bold transition-all duration-300 hover:shadow-2xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              Ligar: {siteInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Publicações */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-teal-100 text-teal-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              Blog
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{posts.title}</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">{posts.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.items.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <p className="text-sm text-teal-600 mb-2 font-semibold">{post.date}</p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link
                    to={post.link}
                    className="inline-flex items-center text-teal-600 hover:text-lime-600 font-semibold transition-colors group"
                  >
                    Saiba mais 
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}</div>
        </div>
      </section>

      {/* Depoimentos Modernizados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-yellow-100 text-yellow-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              ⭐ Avaliações
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{testimonials.title}</h2>
            <div className="flex items-center justify-center space-x-1 text-yellow-400 text-2xl">
              <Star className="w-6 h-6 fill-current" />
              <Star className="w-6 h-6 fill-current" />
              <Star className="w-6 h-6 fill-current" />
              <Star className="w-6 h-6 fill-current" />
              <Star className="w-6 h-6 fill-current" />
              <span className="text-gray-600 text-base ml-2">(4.8 de 5 - 150 avaliações)</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.items.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 relative border border-gray-100"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-400 to-lime-500 rounded-t-2xl"></div>
                <div className="flex items-center space-x-1 text-yellow-400 mb-4">
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                  <Star className="w-5 h-5 fill-current" />
                </div>
                <p className="text-gray-700 italic mb-6 leading-relaxed">\"{testimonial.text}\"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-lime-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-900 font-semibold">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">Cliente Verificado ✓</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Santa Casa Serviço Funerário Imediato - Funerária 24 Horas no Meier, Rio de Janeiro
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-700">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Serviços Funerários Completos no Meier</h3>
                <p className="mb-4">
                  A <strong>Santa Casa Serviço Funerário Imediato</strong> é referência em <strong>serviços funerários no Meier</strong> e 
                  em toda a <strong>Zona Norte do Rio de Janeiro</strong>. Oferecemos atendimento 24 horas com equipe especializada 
                  em <strong>cremação</strong>, <strong>sepultamento</strong>, <strong>translado nacional e internacional</strong>, 
                  velório e apoio ao luto.
                </p>
                <p className="mb-4">
                  Localizada na <strong>Rua Enéas Galvão, 78 - Sala 201, Meier - RJ</strong>, nossa funerária atende famílias com compromisso 
                  humano, oferecendo desde planos funerários até assistência completa em momentos de despedida.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Atendimento Humanizado 24/7</h3>
                <p className="mb-4">
                  Com mais de 18.000 famílias atendidas, a <strong>Santa Casa Serviço Funerário Imediato</strong> oferece:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Cremação 24h no Meier</strong> - Processo completo com documentação e crematórios certificados</li>
                  <li><strong>Translado Funerário</strong> - Nacional e internacional com toda logística</li>
                  <li><strong>Velório e Sepultamento</strong> - Cerimônias personalizadas com respeito às tradições</li>
                  <li><strong>Tanatopraxia</strong> - Preparação digna do corpo com técnicas profissionais</li>
                  <li><strong>Apoio ao Luto</strong> - Suporte emocional e psicológico para familiares</li>
                  <li><strong>Planos Funerários</strong> - Coberturas individuais e familiares</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-br from-teal-50 to-lime-50 rounded-2xl p-6">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Por Que Escolher a Santa Casa no Meier?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <p className="text-5xl font-bold text-teal-600 mb-2">24h</p>
                  <p className="text-gray-700">Atendimento ininterrupto todos os dias</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <p className="text-5xl font-bold text-teal-600 mb-2">18mil+</p>
                  <p className="text-gray-700">Famílias atendidas com excelência</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <p className="text-5xl font-bold text-teal-600 mb-2">100%</p>
                  <p className="text-gray-700">Suporte completo e humanizado</p>
                </div>
              </div>
            </div>

            <p className="mt-6 text-center text-gray-700 bg-gray-50 p-4 sm:p-6 rounded-xl text-sm sm:text-base">
              <strong>Santa Casa Serviço Funerário Imediato 24h no Meier - Zona Norte RJ</strong> | 
              Atendimento imediato: <a href="tel:2141064022" className="text-teal-600 font-bold hover:text-lime-600">(21) 4106-4022</a> | 
              Funerária completa com cremação, translado nacional e internacional, velório 24h, sepultamento, remoção de corpo, documentação funeral, urnas, flores, assistência funeral, plantão funerário e apoio ao luto. Atendemos Meier, Tijuca, Centro, Barra, Zona Norte e toda região do Rio de Janeiro.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
